import { Application } from '@frontend/functionality/types';
import { useAccountRepository, useLicenseRepository } from '@frontend/repository';
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

const SUPPORTED_INSIGHT_APPLICATIONS = [Application.PUDO, Application.VENDING, Application.LENDING, Application.NIVALIZ, Application.HARDWARE] as const;

export interface InsightsOverviewViewProps {
    currentAccount: string;
    tabs: string[];
    showTabs: boolean;
    activeTab: string | null;
    onChange: (tab: string) => void;
    isReady: boolean;
}

const useInsightsOverview = (): InsightsOverviewViewProps => {
    const { currentAccount } = useAccountRepository();
    const { functionalities } = useLicenseRepository();
    const [searchParams, setSearchParams] = useSearchParams();

    const tabs = useMemo((): string[] => {
        const applications = new Set(functionalities.map((functionality) => functionality.application));
        return SUPPORTED_INSIGHT_APPLICATIONS.filter((application) => applications.has(application));
    }, [functionalities]);

    const showTabs = tabs.length > 1;
    const activeTabFromParams = searchParams.get('insight-tag');
    const activeTab = useMemo(() => {
        if (tabs.length === 0) return null;
        if (activeTabFromParams && tabs.includes(activeTabFromParams)) {
            return activeTabFromParams;
        }
        return tabs[0];
    }, [tabs, activeTabFromParams]);

    useEffect(() => {
        if (tabs.length === 0 || !activeTab) return;
        if (activeTabFromParams === activeTab) return;

        setSearchParams(
            (prev) => {
                const search = new URLSearchParams(prev);
                search.set('insight-tag', activeTab);
                return search.toString();
            },
            { replace: true }
        );
    }, [tabs, activeTab, activeTabFromParams, setSearchParams]);

    const onChange = (filter: string) => {
        setSearchParams((prev) => {
            const search = new URLSearchParams(prev);
            search.set('insight-tag', filter.toLowerCase());
            return search.toString();
        });
    };

    return {
        currentAccount,
        tabs,
        showTabs,
        activeTab,
        onChange,
        isReady: true
    };
};

export default useInsightsOverview;
